import { Button, Modal } from '../../../modules/design-system';
import type { ApiClient } from '../../../modules/api';
import { useCancelDelivery, type CancelDeliveryCallbacks } from './useCancelDelivery';

export interface CancelDeliveryModalProps extends CancelDeliveryCallbacks {
  client: ApiClient;
  shippingId: number;
  onClose: () => void;
}

/**
 * Confirmation step for canceling a paid delivery — the request itself lives
 * in {@link useCancelDelivery}; any error but `SHIPPING_NOT_CANCELABLE` keeps
 * the modal open for a retry.
 */
export function CancelDeliveryModal({
  client,
  shippingId,
  onClose,
  onCanceled,
  onNotCancelable,
}: CancelDeliveryModalProps) {
  const { submitting, error, confirm } = useCancelDelivery(client, shippingId, { onCanceled, onNotCancelable });

  return (
    <Modal
      onClose={() => {
        if (!submitting) onClose();
      }}
      inspectName="CancelDeliveryModal"
      ariaLabelledBy="cancel-delivery-title"
    >
      <div
        data-co-component="CancelDeliveryModal"
        className="flex flex-col gap-5 rounded-[24px] p-6"
        style={{
          background: 'var(--co-CancelDeliveryModal-cardFill, #FFFFFF)',
          color: 'var(--co-CancelDeliveryModal-textColor, #15181E)',
        }}
      >
        <div className="flex flex-col gap-2">
          <h2
            id="cancel-delivery-title"
            className="text-[20px] font-bold tracking-[-0.3px]"
          >
            Cancel this delivery?
          </h2>
          <p
            className="text-[15px] leading-[1.4]"
            style={{ color: 'var(--co-CancelDeliveryModal-descColor, rgba(0,0,0,0.75))' }}
          >
            Your cards will return to your collection as sold at their buyback
            price, and the shipping and handling charges will be refunded to
            your balance.
          </p>
        </div>

        {error && (
          <p
            role="alert"
            className="text-[13px] leading-[1.4]"
            style={{ color: 'var(--co-CancelDeliveryModal-errorColor, #E5484D)' }}
          >
            {error}
          </p>
        )}

        <div className="flex flex-col gap-3">
          <Button
            variant="primary"
            size="lg"
            onClick={() => void confirm()}
            loading={submitting}
            fullWidth
          >
            Confirm cancellation
          </Button>
          <Button
            variant="secondary"
            size="lg"
            onClick={onClose}
            disabled={submitting}
            fullWidth
          >
            Keep delivery
          </Button>
        </div>
      </div>
    </Modal>
  );
}
